/**
 * Onboarding Detection Helpers (Phase E1)
 *
 * Automatically detect completed onboarding steps based on user data
 */

/**
 * Get completed steps for business owner (R1)
 * @param {Object} user - User object
 * @param {Object} business - Business object
 * @param {Array} locations - Shop locations array
 * @param {Array} services - Services array
 * @param {Boolean} stripeConnected - Stripe account status
 * @returns {Array} - Array of completed step IDs
 */
export const getOwnerCompletedSteps = (
  user,
  business,
  locations = [],
  services = [],
  stripeConnected = false,
) => {
  const completed = [];

  // Step 1: Profile (business name and type)
  if (business?.name && business?.type?.length > 0) {
    completed.push("profile");
  }

  // Step 2: Logo (business logo uploaded)
  if (business?.logo) {
    completed.push("logo");
  }

  // Step 3: Location (at least one shop location)
  if (locations?.length > 0) {
    completed.push("location");
  }

  // Step 4: Business Hours (at least one location has businessHoursConfigured flag set)
  const hasBusinessHours = locations?.some(
    (loc) => loc.businessHoursConfigured === true,
  );
  if (hasBusinessHours) {
    completed.push("hours");
  }

  // Step 5: Team (at least one R3 professional at any location)
  // This check is delegated to shop-level visibility
  // For onboarding purposes, we'll mark it complete if they have locations with professionals
  // The actual enforcement is at shop visibility level

  // Step 6: Payment (Stripe connected with charges and payouts enabled)
  if (stripeConnected) {
    completed.push("payment");
  }

  return completed;
};

/**
 * Get completed steps for freelancer (R4)
 * @param {Object} user - User object
 * @param {Object} staff - Staff profile object
 * @param {Array} services - Services array
 * @param {Object} availability - Availability schedule
 * @param {Boolean} stripeConnected - Stripe account status
 * @returns {Array} - Array of completed step IDs
 */
export const getFreelancerCompletedSteps = (
  hasPhoto,
  services = [],
  schedule = null,
  stripeConnected = false,
) => {
  const completed = [];

  // Step 1: Photo (brandImage - required for visibility)
  if (hasPhoto) {
    completed.push("photo");
  }

  // Step 2: Services (at least one service with pricing)
  if (services?.length > 0) {
    completed.push("services");
  }

  // Step 3: Schedule (availability set in Schedule collection)
  if (schedule) {
    completed.push("schedule");
  }

  // Step 4: Payment (Stripe connected)
  if (stripeConnected) {
    completed.push("payment");
  }

  return completed;
};

/**
 * Get completed steps for professional (R3)
 * @param {Object} user - User object
 * @param {Object} staff - Staff profile object
 * @param {Array} services - Services array
 * @param {Object} schedule - Weekly schedule
 * @param {Object} bankAccount - Bank account status
 * @returns {Array} - Array of completed step IDs
 */
export const getProfessionalCompletedSteps = (
  hasPhoto,
  services = [],
  schedule = null,
  bankAccount = null,
) => {
  const completed = [];

  // Step 1: Photo (profilePicture - REQUIRED for visibility)
  if (hasPhoto) {
    completed.push("photo");
  }

  // Step 2: Services (at least one service added - REQUIRED for visibility)
  if (services?.length > 0) {
    completed.push("services");
  }

  // Step 3: Schedule (weekly schedule configured in Schedule collection - REQUIRED for visibility)
  if (schedule) {
    completed.push("schedule");
  }

  // Note: Bank account removed from checklist - not required for professional visibility
  // Bank account is optional for R3 professionals (can use business revenue mode)

  return completed;
};

/**
 * Get completed steps for customer (R5)
 * @param {Object} user - User object
 * @param {Number} bookingCount - Number of bookings made
 * @param {Boolean} notificationsEnabled - Notification status
 * @returns {Array} - Array of completed step IDs
 */
export const getCustomerCompletedSteps = (
  user,
  bookingCount = 0,
  notificationsEnabled = false,
) => {
  const completed = [];

  // Step 1: Photo
  if (user?.profilePicture) {
    completed.push("photo");
  }

  // Step 2: Preferences (phone number as proxy for preferences set)
  if (user?.phone) {
    completed.push("preferences");
  }

  // Step 3: Booking (made at least one booking)
  if (bookingCount > 0) {
    completed.push("booking");
  }

  // Step 4: Notifications
  if (notificationsEnabled) {
    completed.push("notification");
  }

  return completed;
};

/**
 * Determine user type from roles array
 * @param {Array} roles - Array of role codes (R1, R2, R3, R4, R5, R6)
 * @returns {String} - 'owner', 'freelancer', 'professional', or 'customer'
 */
export const getUserTypeFromRoles = (roles = []) => {
  if (!roles || roles.length === 0) return "customer";

  // Priority order: owner > freelancer > professional > customer
  if (roles.includes("R1")) return "owner";
  if (roles.includes("R4") || roles.includes("R7")) return "freelancer";
  if (roles.includes("R3")) return "professional";
  return "customer";
};

/**
 * Check if onboarding should be shown
 * @param {Object} onboardingData - Onboarding data from user profile
 * @param {Array} completedSteps - Array of completed step IDs
 * @param {Array} allSteps - Array of all step IDs for user type
 * @returns {Boolean} - True if checklist should be shown
 */
export const shouldShowOnboarding = (
  onboardingData,
  completedSteps = [],
  allSteps = [],
) => {
  // Don't show if marked as complete
  if (onboardingData?.completedAt) return false;

  // If dismissed, check if it's been more than 1 week (7 days)
  if (onboardingData?.dismissedAt) {
    const dismissedDate = new Date(onboardingData.dismissedAt);
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    // Only show again if dismissed more than 1 week ago AND steps are incomplete
    if (dismissedDate > oneWeekAgo) return false;
  }

  // Show if not all steps completed
  return completedSteps.length < allSteps.length;
};
